import React, { useEffect, useState, useMemo } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import {
  Warning, CheckCircle, ArrowsClockwise, Funnel, ShieldWarning,
  Timer, Hourglass, FirstAid, HandWaving, ClockClockwise, Eye,
} from "@phosphor-icons/react";

const STATUS_TABS = [
  { v: "open",   l: "Open",   color: "text-red-400" },
  { v: "ack",    l: "Ack'd",  color: "text-amber-400" },
  { v: "closed", l: "Closed", color: "text-emerald-400" },
];

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  return formatDuration(Math.floor(diff / 1000)) + " ago";
}

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [mttr, setMttr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("open");
  const [subjectType, setSubjectType] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { days };
      if (status) params.status = status;
      if (subjectType) params.subject_type = subjectType;
      if (clientId) params.client_id = clientId;
      const [a, b, c] = await Promise.all([
        api.get("/incidents", { params }),
        api.get("/mttr/summary", { params: { days } }),
        clients.length ? Promise.resolve({ data: clients }) : api.get("/clients"),
      ]);
      setItems(a.data);
      setMttr(b.data);
      if (!clients.length) setClients(c.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status, subjectType, clientId, days]);

  const ack = async (id) => {
    setBusy({ ...busy, [id]: true });
    try {
      await api.post(`/incidents/${id}/ack`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy({ ...busy, [id]: false });
    }
  };

  const clientName = (cid) => clients.find(c => c.id === cid)?.name || "—";

  const perClientSorted = useMemo(() => {
    if (!mttr?.per_client) return [];
    return [...mttr.per_client].sort((a, b) => (b.incidents_closed + b.incidents_open) - (a.incidents_closed + a.incidents_open));
  }, [mttr]);

  return (
    <Layout>
      <PageHeader
        title="Incidents"
        subtitle="Open / ack / closed timeline · MTTR over time"
        actions={
          <button onClick={load} data-testid="incidents-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md transition-colors">
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        }
      />
      <div className="p-6 space-y-6" data-testid="incidents-page">
        {/* MTTR summary cards */}
        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-3">
            MTTR — last {days} days
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MttrCard label="Open" value={mttr?.overall?.incidents_open ?? 0}
              icon={ShieldWarning} color="text-red-400" testid="mttr-open" />
            <MttrCard label="Closed" value={mttr?.overall?.incidents_closed ?? 0}
              icon={CheckCircle} color="text-emerald-400" testid="mttr-closed" />
            <MttrCard label="MTTR"
              value={mttr?.overall?.mttr_seconds != null ? formatDuration(mttr.overall.mttr_seconds) : "—"}
              icon={Timer} color="text-brand-cyan" testid="mttr-overall" />
            <MttrCard label="Window" value={`${days}d`}
              icon={Hourglass} color="text-slate-300" testid="mttr-window" />
          </div>
        </section>

        {/* per-client MTTR */}
        {perClientSorted.length > 0 && (
          <section className="sw-card">
            <header className="px-4 py-3 border-b border-border">
              <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Per-client MTTR</h2>
            </header>
            <table className="w-full text-sm" data-testid="per-client-mttr-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400 bg-background-deep/50">
                  <th className="text-left py-2.5 px-4">Client</th>
                  <th className="text-right py-2.5 px-4">Open</th>
                  <th className="text-right py-2.5 px-4">Closed</th>
                  <th className="text-right py-2.5 px-4">MTTR</th>
                </tr>
              </thead>
              <tbody>
                {perClientSorted.map((r, i) => (
                  <tr key={r.client_id || `un-${i}`} className="border-t border-border hover:bg-white/5">
                    <td className="py-2.5 px-4 text-white" data-testid={`pc-mttr-name-${r.client_id || "none"}`}>
                      {r.client_name}
                    </td>
                    <td className="py-2.5 px-4 text-right text-red-400">{r.incidents_open}</td>
                    <td className="py-2.5 px-4 text-right text-emerald-400">{r.incidents_closed}</td>
                    <td className="py-2.5 px-4 text-right text-brand-cyan">
                      {r.mttr_seconds != null ? formatDuration(r.mttr_seconds) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Filters */}
        <section className="sw-card p-4">
          <div className="flex flex-wrap items-center gap-3" data-testid="incidents-filters">
            <Funnel size={14} className="text-slate-400" />
            <div className="flex gap-1 bg-background-deep border border-border rounded-md p-1" data-testid="incidents-status-tabs">
              {STATUS_TABS.map(t => (
                <button key={t.v} onClick={() => setStatus(t.v)}
                  data-testid={`incidents-tab-${t.v}`}
                  className={`px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] rounded transition-colors ${
                    status === t.v ? "bg-brand-cyan/15 text-brand-cyan" : "text-slate-400 hover:text-white"
                  }`}>
                  {t.l}
                </button>
              ))}
            </div>
            <select value={subjectType} onChange={e => setSubjectType(e.target.value)}
              data-testid="incidents-subject-filter"
              className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200">
              <option value="">All types</option>
              <option value="monitor">Monitors</option>
              <option value="backup_job">Backups</option>
            </select>
            <select value={clientId} onChange={e => setClientId(e.target.value)}
              data-testid="incidents-client-filter"
              className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200">
              <option value="">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              data-testid="incidents-days-filter"
              className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200">
              {[7, 14, 30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              {items.length} incident{items.length === 1 ? "" : "s"}
            </span>
          </div>
        </section>

        {/* List */}
        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : error ? (
          <div className="text-sm text-red-400">{error}</div>
        ) : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <CheckCircle size={36} weight="duotone" className="text-emerald-400 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No incidents in this view</h3>
            <p className="text-sm text-slate-400">All quiet on the {status} front.</p>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="incidents-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Subject</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">Severity</th>
                  <th className="text-left py-3 px-4">Opened</th>
                  <th className="text-left py-3 px-4">Duration</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(inc => (
                  <tr key={inc.id} className="border-t border-border hover:bg-white/5"
                    data-testid={`incident-row-${inc.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {inc.subject_type === "monitor"
                          ? <Eye size={14} className="text-brand-cyan" weight="duotone" />
                          : <FirstAid size={14} className="text-amber-400" weight="duotone" />}
                        <div className="min-w-0">
                          <div className="text-white truncate max-w-[260px]">{inc.subject_name || inc.subject_id}</div>
                          <div className="text-[10px] text-slate-500 truncate max-w-[260px]">
                            {inc.subject_target || (inc.subject_type === "monitor" ? "monitor" : "backup")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300 text-xs">{clientName(inc.client_id)}</td>
                    <td className="py-3 px-4">
                      <SeverityBadge sev={inc.severity} opened={inc.opened_status} />
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-300 whitespace-nowrap">
                      {new Date(inc.opened_at).toLocaleString()}
                      <div className="text-[10px] text-slate-500">{relTime(inc.opened_at)}</div>
                    </td>
                    <td className="py-3 px-4 text-xs text-brand-cyan">
                      {inc.duration_seconds != null
                        ? formatDuration(inc.duration_seconds)
                        : <span className="text-slate-500">ongoing…</span>}
                    </td>
                    <td className="py-3 px-4">
                      <StatusPill status={inc.status} ackBy={inc.ack_by} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      {inc.status === "open" && (
                        <button onClick={() => ack(inc.id)} disabled={busy[inc.id]}
                          data-testid={`incident-ack-${inc.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 rounded font-semibold uppercase tracking-[0.15em] transition-colors">
                          <HandWaving size={12} weight="bold" /> {busy[inc.id] ? "…" : "Ack"}
                        </button>
                      )}
                      {inc.status === "ack" && (
                        <span className="text-[10px] text-slate-400" data-testid={`incident-acked-${inc.id}`}>
                          <ClockClockwise size={11} className="inline mr-0.5" /> acked
                        </span>
                      )}
                      {inc.status === "closed" && (
                        <span className="text-[10px] text-emerald-400">
                          <CheckCircle size={11} weight="fill" className="inline mr-0.5" /> closed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

function MttrCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="sw-card p-4 sw-card-hover">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span>
        {Icon && <Icon size={16} className={color || "text-slate-400"} weight="bold" />}
      </div>
      <div className={`font-display text-3xl font-bold mt-2 ${color || "text-white"}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function SeverityBadge({ sev, opened }) {
  const tone = sev === "down" ? "bg-red-500/15 text-red-300 border-red-500/40"
    : "bg-amber-500/15 text-amber-300 border-amber-500/40";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${tone}`}>
      {opened || sev}
    </span>
  );
}

function StatusPill({ status, ackBy }) {
  if (status === "open") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded bg-red-500/15 text-red-300 border-red-500/40">
        <Warning size={10} weight="bold" /> OPEN
      </span>
    );
  }
  if (status === "ack") {
    return (
      <span className="inline-flex flex-col gap-0">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded bg-amber-500/15 text-amber-300 border-amber-500/40">
          <HandWaving size={10} weight="bold" /> ACK
        </span>
        {ackBy && <span className="text-[9px] text-slate-500 mt-0.5">by {ackBy}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
      <CheckCircle size={10} weight="bold" /> CLOSED
    </span>
  );
}
